import React, { useState, useEffect } from 'react';
import { Search, Download, Calendar, Printer } from 'lucide-react';
import axios from 'axios';
import { generateConsultantReceipt } from '@/utils/consultantReceiptGenerator';

const PaymentReport = () => {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [collegeInfo, setCollegeInfo] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPaymentReport();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchPaymentReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/consultants/reports/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payment report:', error);
      alert('Failed to fetch payment report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.consultant_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!dateFilter.start || !dateFilter.end) return matchesSearch;
    
    const paymentDate = new Date(payment.payment_date);
    const startDate = new Date(dateFilter.start);
    const endDate = new Date(dateFilter.end);
    
    return matchesSearch && paymentDate >= startDate && paymentDate <= endDate;
  });

  const totalPaid = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);

  const handlePrintReceipt = async (payment) => {
    // Format payment data for receipt
    const receiptData = {
      ...payment,
      receipt_no: payment.receipt_no || 'N/A',
      consultant_name: payment.consultant_name || 'N/A',
      consultant_email: payment.consultant_email || 'N/A',
      amount: payment.amount || 0,
      payment_mode: payment.payment_mode || 'N/A',
      payment_date: payment.payment_date || new Date().toISOString(),
      reference_number: payment.reference_number || ''
    };
    
    await generateConsultantReceipt(receiptData, collegeInfo);
  };

  const exportToCSV = () => {
    const headers = ['Receipt No', 'Consultant Name', 'Email', 'Amount', 'Payment Date', 'Payment Mode', 'Payment Made By', 'Reference Number'];
    const csvData = filteredPayments.map(payment => [
      payment.receipt_no || 'N/A',
      payment.consultant_name,
      payment.consultant_email,
      payment.amount,
      payment.payment_date,
      payment.payment_mode,
      payment.created_by_name || payment.created_by || 'Admin',
      payment.reference_number || 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Report</h1>
          <p className="text-sm text-gray-500 mt-1">Consultant payment history and records</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Summary Card */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Payments Made</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">₹{totalPaid.toLocaleString('en-IN')}</p>
            <p className="text-sm text-gray-500 mt-1">{filteredPayments.length} transactions</p>
          </div>
          <Calendar className="h-16 w-16 text-blue-500" />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by consultant name or email..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={dateFilter.start}
              onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
              placeholder="Start Date"
            />
          </div>
          <div>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={dateFilter.end}
              onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
              placeholder="End Date"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consultant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Made By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No payments found</td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payment.receipt_no || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{payment.consultant_name}</div>
                      <div className="text-sm text-gray-500">{payment.consultant_email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">Rs. {payment.amount.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{payment.payment_date}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {payment.payment_mode}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.created_by_name || payment.created_by || 'Admin'}</div>
                      <div className="text-xs text-gray-500">{payment.created_by_role || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handlePrintReceipt(payment)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        title="Print Receipt"
                      >
                        <Printer size={16} />
                        Print
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentReport;